import { Dispatch, FunctionComponent, MouseEvent, SetStateAction } from "react";

import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { EmailIcon } from "@/components/EmailIcon/EmailIcon";
import { getEmailIconStatus } from "@/components/EmailIcon/getEmailIconStatus";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { PhoneIcon } from "@/components/PhoneIcon/PhoneIcon";
import { getPhoneIconStatus } from "@/components/PhoneIcon/getPhoneIconStatus";
import { ProspectScoreBadge } from "@/components/ProspectScoreBadge/ProspectScoreBadge";
import { Spinner } from "@/components/Spinner/Spinner";

import { classNames } from "@/utils/classNames";

import { OrganizationProfile } from "../../../schema/organization-profiles";

interface ProfileCardProps {
	profile: OrganizationProfile;
	failedImages: Set<number>;
	setFailedImages: Dispatch<SetStateAction<Set<number>>>;
	addIsLoading: boolean;
	onAdd: () => void;
	/**
	 * Open this colleague's own lead panel. Omitted by hosts that have nowhere to open one — the
	 * card is then inert rather than offering a click that does nothing (PREC-4116).
	 */
	onSelect?: () => void;
}

const stopClickThrough = (event: MouseEvent): void => {
	event.stopPropagation();
};

/**
 * One colleague on the lead panel's Profiles tab: the persona score badge, name and title, the
 * LinkedIn / email / phone status icons, an "Add to list" action for somebody not on the current
 * list, and the lists they already sit on.
 *
 * All three channel icons are always rendered, badged from the same helpers the interactive
 * {@link ContactChannels} row uses — a missing icon and a "not found" icon would otherwise be the
 * same picture, and "nobody has looked yet" is a different answer again.
 */
export const ProfileCard: FunctionComponent<ProfileCardProps> = ({
	profile,
	failedImages,
	setFailedImages,
	addIsLoading,
	onAdd,
	onSelect,
}) => {
	const { t } = useTranslation();
	const showImage = !!profile.image && !failedImages.has(profile.prospectId);

	return (
		<div
			className={classNames(
				"flex w-full flex-col rounded-xl border border-border-light",
				onSelect ? "cursor-pointer hover:border-brand-light" : ""
			)}
			data-testid={`profile-card-${profile.prospectId}`}
			role={onSelect ? "button" : undefined}
			tabIndex={onSelect ? 0 : undefined}
			onClick={onSelect}
			onKeyDown={(event) => {
				if (onSelect && (event.key === "Enter" || event.key === " ")) {
					event.preventDefault();
					onSelect();
				}
			}}
		>
			<div className="flex flex-row items-center justify-between p-3">
				<div className="flex min-w-0 flex-row items-center gap-3">
					<div className="relative shrink-0">
						{showImage ? (
							<img
								alt={profile.name || ""}
								className="h-[40px] w-[40px] shrink-0 rounded-full"
								src={profile.image ?? undefined}
								onError={() => {
									setFailedImages((prev) => new Set([...prev, profile.prospectId]));
								}}
							/>
						) : (
							<div className="h-[40px] w-[40px] shrink-0 rounded-full bg-gray-200" />
						)}
						{/* Positioned wrapper: the badge's Tooltip root is itself `relative`, so absolute
						    classes on the badge would anchor to the tooltip, not the avatar. */}
						<span className="absolute -bottom-1 -right-1.5">
							<ProspectScoreBadge
								className="border-2 border-white"
								overallScore={profile.overallScore}
								reason={profile.overallReasoning}
								size="sm"
								variant="solid"
							/>
						</span>
					</div>
					<div className="flex min-w-0 flex-col">
						<span className="truncate text-sm font-semibold text-text">{profile.name}</span>
						{profile.title && (
							<span className="truncate text-xs text-text-meta">{profile.title}</span>
						)}
					</div>
				</div>
				<div className="flex shrink-0 items-center gap-2">
					<span className="inline-flex flex-row items-center" onClick={stopClickThrough}>
						{profile.linkedinUrl ? (
							<a
								aria-label="linkedin"
								href={profile.linkedinUrl}
								referrerPolicy="no-referrer"
								rel="noreferrer"
								target="_blank"
							>
								<LinkedinIcon status="checked" />
							</a>
						) : (
							<LinkedinIcon status="failed" />
						)}
						<EmailIcon status={getEmailIconStatus(profile.email, profile.emailStatus)} />
						<PhoneIcon status={getPhoneIconStatus(profile.phone, profile.phoneStatus)} />
					</span>
					{!profile.isOnCurrentList && (
						<div className="ml-2" onClick={stopClickThrough}>
							{addIsLoading ? (
								<Spinner color="#4d66f5" />
							) : (
								<Button size={ButtonSize.S} title={t("list.addToList")} onClick={onAdd} />
							)}
						</div>
					)}
				</div>
			</div>
			{profile.lists.length > 0 && (
				<div className="flex flex-wrap gap-1 px-3 pb-3">
					{profile.lists.map((list) => (
						<span
							key={list.listId}
							className="bg-gray-100 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-text-meta"
						>
							{list.listName}
						</span>
					))}
				</div>
			)}
		</div>
	);
};
